import { ScheduledGame } from "src/types/TournamentTypes";
import { TOURNAMENT_COLORS, LEAGUE_CHIP_COLORS } from "./TournamentConstants";
import { ApprovedTeam } from "src/types/TournamentTypes";
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Box, Stack, Chip, Typography, Tooltip } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

function GameCard({
    game,
    teamName,
    leagueName,
    tournamentName,
    color,
    leagueChipColor,
    allScheduledGames,
    teamsMap,
    getPlayerIdsOnTeam,
    onGameClick,
    height,
    rowSpan,
    leftPercent,
    widthPercent,
}: {
    game: ScheduledGame;
    teamName: (id: number) => string;
    leagueName: string;
    tournamentName: string;
    color: typeof TOURNAMENT_COLORS[0];
    leagueChipColor: typeof LEAGUE_CHIP_COLORS[0];
    allScheduledGames: ScheduledGame[];
    teamsMap: Record<number, ApprovedTeam>;
    getPlayerIdsOnTeam: (id: number) => number[];
    onGameClick: (game: ScheduledGame) => void;
    height: number;
    rowSpan: number;
    leftPercent: number;
    widthPercent: number;
}) {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({
            id: `game-${game.scheduled_event_id}`,
        });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab",
    };

    /** Detailed conflict info: which players are double-booked and in which games */
    const conflictDetails = useMemo(() => {
        const gameStart = dayjs(`${game.game_date}T${game.game_time}`);
        const gameEnd = gameStart.add(game.duration_minutes, "minute");

        const thisPlayerIds = [
            ...getPlayerIdsOnTeam(game.home_team_id),
            ...getPlayerIdsOnTeam(game.away_team_id),
        ];
        const thisPlayerIdSet = new Set(thisPlayerIds);

        // Build a quick id→name lookup from the teams on this game
        const playerNameById: Record<number, string> = {};
        [game.home_team_id, game.away_team_id].forEach((tid) => {
            const team = teamsMap[tid];
            if (team) team.players.forEach((p: { player_id: number; first_name: string; last_name: string }) => {
                playerNameById[p.player_id] = `${p.first_name} ${p.last_name}`;
            });
        });

        const conflicts: { playerName: string; otherGame: string }[] = [];

        allScheduledGames.forEach((other) => {
            if (other.scheduled_event_id === game.scheduled_event_id) return;
            if (other.game_date !== game.game_date) return;
            const oStart = dayjs(`${other.game_date}T${other.game_time}`);
            const oEnd = oStart.add(other.duration_minutes, "minute");
            if (!(oStart.isBefore(gameEnd) && gameStart.isBefore(oEnd))) return;

            const otherPlayerIds = [
                ...getPlayerIdsOnTeam(other.home_team_id),
                ...getPlayerIdsOnTeam(other.away_team_id),
            ];

            // Also grab names from the other game's teams
            [other.home_team_id, other.away_team_id].forEach((tid) => {
                const team = teamsMap[tid];
                if (team) team.players.forEach((p: { player_id: number; first_name: string; last_name: string }) => {
                    playerNameById[p.player_id] = `${p.first_name} ${p.last_name}`;
                });
            });

            const otherLabel = `${teamName(other.home_team_id)} vs ${teamName(other.away_team_id)} (${oStart.format("h:mm A")})`;

            otherPlayerIds.forEach((pid) => {
                if (thisPlayerIdSet.has(pid)) {
                    conflicts.push({
                        playerName: playerNameById[pid] ?? `Player #${pid}`,
                        otherGame: otherLabel,
                    });
                }
            });
        });

        return conflicts;
    }, [game, allScheduledGames, getPlayerIdsOnTeam, teamsMap, teamName]);

    const hasConflicts = conflictDetails.length > 0;

    const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null);

    const handleMouseDown = (e: React.MouseEvent) => {
        setDragStartPos({ x: e.clientX, y: e.clientY });
    };

    const handleClick = (e: React.MouseEvent) => {
        // Only open dialog if not dragging and not clicking on buttons
        if (isDragging) return;
        const target = e.target as HTMLElement;
        // Don't open if clicking on delete/edit buttons
        if (target.closest("button") || target.closest(".MuiIconButton-root")) {
            return;
        }
        // Check if mouse moved significantly (indicating a drag)
        if (dragStartPos) {
            const dx = Math.abs(e.clientX - dragStartPos.x);
            const dy = Math.abs(e.clientY - dragStartPos.y);
            if (dx > 5 || dy > 5) {
                // Mouse moved significantly, likely a drag
                return;
            }
        }
        e.stopPropagation();
        onGameClick(game);
        setDragStartPos(null);
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            {...listeners}
            {...attributes}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
            sx={{
                backgroundColor: hasConflicts ? "warning.light" : color.bg,
                color: hasConflicts ? "warning.contrastText" : color.text,
                borderRadius: 1,
                p: 0.5,
                position: "absolute",
                top: 2,
                left: `${leftPercent}%`,
                width: `calc(${widthPercent}% - 4px)`,
                height: `${height}px`,
                minHeight: `${height}px`,
                maxHeight: `${height}px`,
                zIndex: isDragging ? 1000 : 10,
                border: `1px solid ${hasConflicts ? "warning.main" : color.border}`,
                cursor: isDragging ? "grabbing" : "pointer",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                    opacity: 0.9,
                    boxShadow: 2,
                },
                "& .MuiIconButton-root": {
                    pointerEvents: "auto",
                },
            }}
        >
            <Stack spacing={0} sx={{ height: "100%", overflow: "hidden", justifyContent: "center" }}>
                <Stack direction="row" gap={0.5} alignItems="center">
                    <Chip
                        label={leagueName.substring(0, 10)}
                        size="small"
                        sx={{
                            height: 14,
                            fontSize: "0.6rem",
                            backgroundColor: leagueChipColor.bg,
                            color: leagueChipColor.text,
                            "& .MuiChip-label": { px: 0.5 },
                        }}
                    />
                
                {game.duration_minutes > 30 ? (
                    <Stack spacing={0}>
                        <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1.1 }}
                            noWrap
                        >
                            {teamName(game.home_team_id)}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1.1 }}
                            noWrap
                        >
                            vs {teamName(game.away_team_id)}
                        </Typography>
                    </Stack>
                ) : (
                    <Typography
                        variant="caption"
                        sx={{ fontSize: "0.65rem", fontWeight: 600, lineHeight: 1.1 }}
                        noWrap
                    >
                        {teamName(game.home_team_id)} vs {teamName(game.away_team_id)}
                    </Typography>
                )}
                {hasConflicts && (
                    <Tooltip
                        arrow
                        placement="top"
                        title={
                            <Box sx={{ p: 0.5, maxWidth: 280 }}>
                                <Typography variant="caption" fontWeight={700} sx={{ display: "block", mb: 0.5 }}>
                                    Player Conflict{conflictDetails.length > 1 ? "s" : ""}
                                </Typography>
                                {/* Deduplicate by player name */}
                                {[...new Map(conflictDetails.map((c) => [c.playerName + c.otherGame, c])).values()].map(
                                    (c, i) => (
                                        <Typography key={i} variant="caption" sx={{ display: "block", lineHeight: 1.3 }}>
                                            • <strong>{c.playerName}</strong> also in {c.otherGame}
                                        </Typography>
                                    )
                                )}
                            </Box>
                        }
                    >
                        <WarningAmberIcon
                            sx={{
                                position: "absolute",
                                top: 2,
                                right: 2,
                                fontSize: 14,
                                color: "warning.dark",
                                cursor: "help",
                            }}
                        />
                    </Tooltip>
                )}
                </Stack>
            </Stack>
        </Box>
    );
}

export default GameCard;